import { useLocation, useNavigate } from "react-router-dom";
import Router from "./routes";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { useAuthStore } from "./store/useAuthStore";
import { useEffect, useRef, useCallback } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import ScrollToTop from "./utils/ScrollToTop";
import { toast, ToastContainer } from "react-toastify";
import { queryClient } from "./queries/client";
import { checkTokenValidity } from "./utils/auth";
import { ErrorBoundary } from "./components/ErrorBoundary";

function App() {
  const { email, isAuthenticated, tokenExpiry } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const expiryWarningShown = useRef(false);

  useEffect(() => {
    // Initialize dark mode based on user preference
    const savedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;

    if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add("dark");
    }
  }, []);

  // Validate auth on every route change — redirect to login if token is missing/expired
  useEffect(() => {
    const isAuthRoute = [
      "/login",
      "/signup",
      "/forgotPassword",
      "/changePassword",
      "/verify-email",
      "/deactivated",
    ].includes(location.pathname);

    if (!isAuthRoute) {
      const token = localStorage.getItem("accesstoken");
      if (!token || !checkTokenValidity()) {
        useAuthStore.getState().clear();
        navigate("/login", { replace: true });
        return;
      }
    }

    if (isAuthenticated && !email && !isAuthRoute) {
      navigate("/login", { replace: true });
    }
  }, [email, isAuthenticated, location.pathname, navigate]);

  // Token expiry warning — warn 10 minutes before expiry
  const checkExpiry = useCallback(() => {
    if (!isAuthenticated || !tokenExpiry) return;
    const timeLeft = tokenExpiry - Date.now();
    const TEN_MINUTES = 10 * 60 * 1000;
    if (timeLeft > 0 && timeLeft <= TEN_MINUTES && !expiryWarningShown.current) {
      expiryWarningShown.current = true;
      const minutesLeft = Math.ceil(timeLeft / 60000);
      toast.warn(`Your session will expire in ${minutesLeft} minute${minutesLeft > 1 ? "s" : ""}. Please save your work.`, {
        autoClose: false,
        toastId: "session-expiry-warning",
      });
    }
    if (timeLeft <= 0) {
      useAuthStore.getState().clear();
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, tokenExpiry, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      expiryWarningShown.current = false;
      return;
    }
    const interval = setInterval(checkExpiry, 60000); // check every minute
    checkExpiry(); // initial check
    return () => clearInterval(interval);
  }, [isAuthenticated, checkExpiry]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ScrollToTop />
        <Router />
        <ToastContainer
          position="bottom-right"
          autoClose={2000}
          theme="colored"
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
